/**
 * JHA Service — real Firestore version
 * Firestore path: organizations/{orgId}/jha/{id}
 */
import { collection, doc, addDoc, updateDoc, getDoc, query, where, orderBy, onSnapshot } from 'firebase/firestore'
import { db } from '../firebase.config'

// ---------------------------------------------------------------------------
// Create a new JHA
// ---------------------------------------------------------------------------
export async function createJHA(orgId: string, data: any) {
  const docRef = await addDoc(collection(db, 'organizations', orgId, 'jha'), {
    orgId,
    ...data,
    status: 'draft',
    crewSignatures: [],
    visitorSignatures: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  })
  return docRef.id
}

// ---------------------------------------------------------------------------
// Get all JHAs (real-time)
// ---------------------------------------------------------------------------
export function subscribeToJHAs(orgId: string, callback: (data: any[]) => void) {
  const q = query(
    collection(db, 'organizations', orgId, 'jha'),
    orderBy('createdAt', 'desc')
  )
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  })
}

// ---------------------------------------------------------------------------
// Get single JHA by ID (real-time)
// ---------------------------------------------------------------------------
export function subscribeToJHA(orgId: string, jhaId: string, callback: (data: any | null) => void) {
  return onSnapshot(doc(db, 'organizations', orgId, 'jha', jhaId), snap => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null)
  })
}

// ---------------------------------------------------------------------------
// Add crew signature
// ---------------------------------------------------------------------------
export async function addCrewSignature(orgId: string, jhaId: string, signature: any) {
  const snap = await getDoc(doc(db, 'organizations', orgId, 'jha', jhaId))
  if (!snap.exists()) return
  const current = snap.data().crewSignatures || []
  if (current.find((s: any) => s.userId === signature.userId)) return
  await updateDoc(doc(db, 'organizations', orgId, 'jha', jhaId), {
    crewSignatures: [...current, { ...signature, signedAt: new Date().toISOString(), pinVerified: true }],
    updatedAt: new Date().toISOString()
  })
}

// ---------------------------------------------------------------------------
// Add visitor signature
// ---------------------------------------------------------------------------
export async function addVisitorSignature(orgId: string, jhaId: string, visitor: any) {
  const snap = await getDoc(doc(db, 'organizations', orgId, 'jha', jhaId))
  if (!snap.exists()) return
  const current = snap.data().visitorSignatures || []
  await updateDoc(doc(db, 'organizations', orgId, 'jha', jhaId), {
    visitorSignatures: [...current, { ...visitor, acknowledgedAt: new Date().toISOString() }],
    updatedAt: new Date().toISOString()
  })
}

// ---------------------------------------------------------------------------
// Update JHA status
// ---------------------------------------------------------------------------
export async function updateJHAStatus(orgId: string, jhaId: string, status: string) {
  await updateDoc(doc(db, 'organizations', orgId, 'jha', jhaId), {
    status,
    updatedAt: new Date().toISOString()
  })
}

// ---------------------------------------------------------------------------
// Close JHA
// ---------------------------------------------------------------------------
export async function closeJHA(orgId: string, jhaId: string) {
  await updateDoc(doc(db, 'organizations', orgId, 'jha', jhaId), {
    status: 'closed',
    updatedAt: new Date().toISOString()
  })
}
